import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link } from 'react-router-dom';

import Message from '../components/Message';
import { Context } from '../components/Store';
import { deleteStockage, getStockages } from '../functions/stockage';

const CODE_REGEX = /^([A-Z]+)-([A-Z])(\d)$/;

export default function Stockages() {
  const [state] = useContext(Context);
  const [stockages, setStockages] = useState([]);
  const [search, setSearch] = useState('');
  const [filterColonne, setFilterColonne] = useState('');
  const [filterNiveau, setFilterNiveau] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [warning, setWarning] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const fetchStockages = useCallback(async () => {
    setIsLoading(true);
    const fetchedStockages = await getStockages(state.token);
    setIsLoading(false);
    if (fetchedStockages.error) return setError(fetchedStockages.error);
    return setStockages(fetchedStockages.data);
  }, [state.token]);

  useEffect(() => {
    fetchStockages();
    if (localStorage.getItem('message')) {
      setInfo(localStorage.getItem('message'));
      localStorage.removeItem('message');
    }
  }, [fetchStockages]);

  const parsedStockages = useMemo(
    () =>
      stockages.map((s) => {
        const match = CODE_REGEX.exec(s.nom);
        return {
          ...s,
          colonne: match ? match[2] : null,
          niveau: match ? match[3] : null,
        };
      }),
    [stockages]
  );

  const colonnesDisponibles = useMemo(
    () =>
      [
        ...new Set(parsedStockages.map((s) => s.colonne).filter(Boolean)),
      ].sort(),
    [parsedStockages]
  );

  const niveauxDisponibles = useMemo(
    () =>
      [...new Set(parsedStockages.map((s) => s.niveau).filter(Boolean))].sort(),
    [parsedStockages]
  );

  const filteredStockages = useMemo(() => {
    return parsedStockages.filter((s) => {
      if (
        search.trim() !== '' &&
        !s.nom.toUpperCase().includes(search.trim().toUpperCase())
      )
        return false;
      if (filterColonne !== '' && s.colonne !== filterColonne) return false;
      if (filterNiveau !== '' && s.niveau !== filterNiveau) return false;
      return true;
    });
  }, [parsedStockages, search, filterColonne, filterNiveau]);

  const resetFilters = () => {
    setSearch('');
    setFilterColonne('');
    setFilterNiveau('');
  };

  const deleteFromList = async (id, nom = id) => {
    const res = window.prompt(
      `Confirmez la suppression de la zone de stockage ${nom} en renseignant son nom ci-dessous. Cette action est définitive.`
    );
    if (res === nom) {
      setIsLoading(true);
      const deleted = await deleteStockage(id, state.token);
      setIsLoading(false);
      if (deleted.error) return setError(deleted.error);
      fetchStockages();
      setError('');
      setInfo('La zone de stockage a bien été supprimée.');
    } else if (typeof res === 'string' || res > 0) {
      setError('Le nom de la zone ne correspond pas.');
    }
  };

  document.title = 'Zones de stockage';

  return (
    <div className="flex flex-col items-center">
      <div className="w-full lg:w-11/12 mb-10">
        <h1 className="mt-6 text-2xl sm:text-3xl md:text-4xl uppercase font-bold mb-8">
          Gestion des zones de stockage
        </h1>
        {error ? <Message message={error} setError={setError} /> : null}
        {warning ? (
          <Message message={warning} type="warning" setError={setWarning} />
        ) : null}
        {info ? (
          <Message message={info} type="success" setError={setInfo} />
        ) : null}
        <div className="bg-viguierBleu rounded">
          <div className="bg-viguierBleuClair text-white rounded-t">
            <div className="flex items-center justify-between py-2 font-bold bg-viguierBleu text-white rounded-t">
              <div className="flex w-1/3" />
              <div className="flex w-1/3 justify-center">
                <h2 className="mr-15">Liste des zones de stockage</h2>
              </div>
              <div className="flex w-1/3 mr-2 justify-end ml-5 lg:ml-auto">
                <Link
                  to="/stockages/new"
                  className="border py-1 px-2 rounded border-white hover:bg-white hover:text-viguierBleu cursor-pointer"
                >
                  Nouvelle zone
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-viguierBleuClair mt-5 mb-5 ml-5 mr-5 rounded-md p-4 flex flex-wrap items-end gap-4">
            <div className="flex flex-col">
              <label htmlFor="search" className="text-white text-sm mb-1">
                Recherche
              </label>
              <input
                id="search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Ex: AT-D2"
                className="border-2 border-stbSecond rounded pl-2 py-1 text-black w-40"
              />
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="filterColonne"
                className="text-white text-sm mb-1"
              >
                Colonne
              </label>
              <select
                id="filterColonne"
                value={filterColonne}
                onChange={(e) => setFilterColonne(e.target.value)}
                className="border-2 border-stbSecond rounded pl-2 py-1 text-black w-28"
              >
                <option value="">Toutes</option>
                {colonnesDisponibles.map((c) => (
                  <option value={c} key={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label htmlFor="filterNiveau" className="text-white text-sm mb-1">
                Niveau
              </label>
              <select
                id="filterNiveau"
                value={filterNiveau}
                onChange={(e) => setFilterNiveau(e.target.value)}
                className="border-2 border-stbSecond rounded pl-2 py-1 text-black w-28"
              >
                <option value="">Tous</option>
                {niveauxDisponibles.map((n) => (
                  <option value={n} key={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={resetFilters}
              className="border py-1 px-2 rounded border-white text-white hover:bg-white hover:text-viguierBleu cursor-pointer h-9"
            >
              Réinitialiser
            </button>
            <span className="text-white text-sm ml-auto">
              {filteredStockages.length} / {stockages.length} zones affichées
            </span>
          </div>

          {isLoading ? (
            <p className="text-center text-white pb-4">Chargement...</p>
          ) : filteredStockages.length === 0 ? (
            <p className="text-center text-white pb-4">
              Aucune zone de stockage ne correspond à ces critères.
            </p>
          ) : (
            <div className="flex flex-wrap gap-3 px-5 pb-5">
              {filteredStockages.map((stockage) => (
                <div
                  key={stockage._id}
                  className="bg-white rounded-md shadow-md overflow-hidden w-24"
                >
                  <div className="bg-viguierBleuClair text-white text-center font-semibold py-2 uppercase text-sm">
                    {stockage.nom}
                  </div>
                  <div className="bg-viguierBleu flex items-center justify-center gap-3 py-1">
                    <Link
                      to={`/stockages/${stockage._id}`}
                      className="text-white hover:text-viguierJaune"
                    >
                      <EditIcon fontSize="small" />
                    </Link>
                    <DeleteIcon
                      fontSize="small"
                      className="text-white hover:text-viguierJaune cursor-pointer"
                      onClick={() => deleteFromList(stockage._id, stockage.nom)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="bg-viguierBleu text-white justify-start flex rounded-b pl-2 py-2" />
        </div>
      </div>
    </div>
  );
}
